import { PeriodSelectorPeriods } from 'adapter/in/common/form/types';
import { ReportInstanceVisibility } from 'domain/entity/reportInstance';
import { Option, none, some, isNone } from 'fp-ts/lib/Option';

export interface EmailRecipient {
    userId: string;
    email: string;
}

export interface CreateOrPreviewAutomationCriteria {
    templateDashboardId: string;
    language: string;
    locationIds: string[];
    locationTags: string[];
    start: string;
    end: string;
    periodicity: Periodicity;
    delayDays: string;
    reportingVisibility: ReportInstanceVisibility;
    logoImageUrl: string;
    emailRecipients: EmailRecipient[];
}

class CreateOrPreviewAutomationCriteriaImpl implements CreateOrPreviewAutomationCriteria {
    constructor(
        readonly templateDashboardId: string,
        readonly language: string,
        readonly locationIds: string[],
        readonly locationTags: string[],
        readonly start: string,
        readonly end: string,
        readonly periodicity: Periodicity,
        readonly delayDays: string,
        readonly reportingVisibility: ReportInstanceVisibility,
        readonly logoImageUrl: string,
        readonly emailRecipients: EmailRecipient[]
    ) {}
}

export class Periodicity {
    constructor(readonly amount: string, readonly resolution: PeriodSelectorPeriods) {}
}

export class CreateOrPreviewAutomationCriteriaBuilder {
    private templateDashboardId: Option<string> = none;

    private language: Option<string> = none;

    private locationIds: Option<string[]> = none;

    private locationTags: Option<string[]> = none;

    private start: Option<string> = none;

    private end: Option<string> = none;

    private periodicityAmount: Option<string> = none;

    private periodicityResolution: Option<PeriodSelectorPeriods> = none;

    private delayDays: Option<string> = none;

    private reportingVisibility: Option<ReportInstanceVisibility> = none;

    private logoImageUrl = '';

    private emailRecipients: EmailRecipient[] = [];

    static create = (): CreateOrPreviewAutomationCriteriaBuilder => new CreateOrPreviewAutomationCriteriaBuilder();

    withTemplateDashboardId = (id: string): CreateOrPreviewAutomationCriteriaBuilder => {
        this.templateDashboardId = some(id);
        return this;
    };

    withLanguage = (lang: string): CreateOrPreviewAutomationCriteriaBuilder => {
        this.language = some(lang);
        return this;
    };

    withLocationIds = (ids: string[]): CreateOrPreviewAutomationCriteriaBuilder => {
        this.locationIds = some(ids);
        return this;
    };

    withLocationTags = (tags: string[]): CreateOrPreviewAutomationCriteriaBuilder => {
        this.locationTags = some(tags);
        return this;
    };

    withStart = (start: string): CreateOrPreviewAutomationCriteriaBuilder => {
        this.start = some(start);
        return this;
    };

    withEnd = (end: string): CreateOrPreviewAutomationCriteriaBuilder => {
        this.end = some(end);
        return this;
    };

    withPeriodicityAmount = (amount: string): CreateOrPreviewAutomationCriteriaBuilder => {
        this.periodicityAmount = some(amount);
        return this;
    };

    withPeriodicityResolution = (res: PeriodSelectorPeriods): CreateOrPreviewAutomationCriteriaBuilder => {
        this.periodicityResolution = some(res);
        return this;
    };

    withDelayDays = (days: string): CreateOrPreviewAutomationCriteriaBuilder => {
        this.delayDays = some(days);
        return this;
    };

    withReportingVisibility = (visibility: ReportInstanceVisibility): CreateOrPreviewAutomationCriteriaBuilder => {
        this.reportingVisibility = some(visibility);
        return this;
    };

    withLogoImageUrl = (url: string): CreateOrPreviewAutomationCriteriaBuilder => {
        this.logoImageUrl= url;
        return this;
    };

    withEmailRecipients = (recipients: EmailRecipient[]): CreateOrPreviewAutomationCriteriaBuilder => {
        this.emailRecipients = recipients;
        return this;
    };

    build = (): CreateOrPreviewAutomationCriteria => {
        if (
            isNone(this.templateDashboardId) ||
            isNone(this.language) ||
            isNone(this.locationIds) ||
            isNone(this.start) ||
            isNone(this.end) ||
            isNone(this.locationTags) ||
            isNone(this.periodicityAmount) ||
            isNone(this.periodicityResolution) ||
            isNone(this.delayDays) ||
            isNone(this.reportingVisibility)
        ) {
            throw new ReferenceError('Missing required properties');
        }

        return new CreateOrPreviewAutomationCriteriaImpl(
            this.templateDashboardId.value,
            this.language.value,
            this.locationIds.value,
            this.locationTags.value,
            this.start.value,
            this.end.value,
            new Periodicity(this.periodicityAmount.value, this.periodicityResolution.value),
            this.delayDays.value,
            this.reportingVisibility.value,
            this.logoImageUrl,
            this.emailRecipients
        );
    };
}
